import * as React from "react"
import { motion, AnimatePresence } from "motion/react"
import confetti from "canvas-confetti"
import { Flame, X, Gift, Loader2, Trophy, Ticket, Star, Sparkles } from "lucide-react"

/* ─── Types ─── */

export interface StreakDay {
  date: string
  claimed: boolean
  isMilestone?: boolean
  milestoneReward?: { type: string; amount: number }
}

export interface StreakData {
  streakId: string
  streakName: string
  currentStreak: number
  canClaimToday: boolean
  todayReward: { type: string; amount: number }
  nextMilestone: { day: number; reward: { type: string; amount: number } } | null
  history: StreakDay[]
}

interface StreakPopupProps {
  isOpen: boolean
  onClose: () => void
  streakData: StreakData | null
  onClaim: (streakId: string) => Promise<{ success: boolean; newStreak?: number; reward?: { type: string; amount: number }; message?: string }>
}

/* ─── Helpers ─── */

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

const getRewardLabel = (type: string, amount: number) => {
  switch (type) {
    case "free_spin": return `${amount} Free Spin${amount > 1 ? "s" : ""}`
    case "raffle_ticket": return `${amount} Raffle Ticket${amount > 1 ? "s" : ""}`
    case "badge": return "Badge"
    case "points": return `${amount.toLocaleString()} pts`
    default: return `${amount}`
  }
}

const isToday = (dateStr: string) => dateStr === new Date().toISOString().split("T")[0]

const fireConfetti = () => {
  const colors = ["#FFD700", "#F37020", "#FFA500", "#FF6347", "#FFE4B5"]
  confetti({ particleCount: 120, spread: 100, origin: { y: 0.5, x: 0.5 }, colors })
  setTimeout(() => {
    confetti({ particleCount: 60, angle: 60, spread: 50, origin: { x: 0, y: 0.65 }, colors })
    confetti({ particleCount: 60, angle: 120, spread: 50, origin: { x: 1, y: 0.65 }, colors })
  }, 150)
}

/* ─── Calendar Day Cell ─── */

function CalendarDay({ day, index, isCurrentDay }: { day: StreakDay; index: number; isCurrentDay: boolean }) {
  const dayNum = new Date(day.date + "T12:00:00").getDate()
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.015, type: "spring", bounce: 0.3 }}
      className={[
        "relative flex flex-col items-center justify-center rounded-lg p-1.5 min-h-[52px] transition-all",
        isCurrentDay ? "bg-gradient-to-br from-[#FFD700]/20 to-[#F37020]/20 border-2 border-[#FFD700] shadow-[0_0_12px_rgba(255,215,0,0.4)]"
          : day.claimed ? "bg-[#F37020]/15 border border-[#F37020]/30"
          : "bg-white/[0.03] border border-white/[0.06]",
      ].join(" ")}
    >
      <span className={["text-[10px] font-medium", isCurrentDay ? "text-[#FFD700]" : day.claimed ? "text-[#F37020]" : "text-white/30"].join(" ")}>{dayNum}</span>
      {day.claimed ? (
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", bounce: 0.5 }} className="mt-0.5"><Flame className="w-4 h-4 text-[#F37020]" /></motion.div>
      ) : isCurrentDay ? (
        <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 1.5, repeat: Infinity }} className="mt-0.5"><Gift className="w-4 h-4 text-[#FFD700]" /></motion.div>
      ) : (
        <div className="w-4 h-4 mt-0.5 rounded-full bg-white/[0.05]" />
      )}
      {day.isMilestone && <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#FFD700] flex items-center justify-center"><Star className="w-2 h-2 text-black" /></div>}
    </motion.div>
  )
}

/* ─── Main Component ─── */

export function StreakPopup({ isOpen, onClose, streakData, onClaim }: StreakPopupProps) {
  const [claiming, setClaiming] = React.useState(false)
  const [claimed, setClaimed] = React.useState(false)
  const [claimResult, setClaimResult] = React.useState<{ type: string; amount: number } | null>(null)

  React.useEffect(() => { if (isOpen) { setClaimed(false); setClaimResult(null) } }, [isOpen])

  const handleClaim = async () => {
    if (!streakData || claiming || claimed) return
    setClaiming(true)
    const result = await onClaim(streakData.streakId)
    if (result.success && result.reward) { setClaimed(true); setClaimResult(result.reward); fireConfetti() }
    setClaiming(false)
  }

  if (!streakData) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
          <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} transition={{ type: "spring", bounce: 0.3 }} className="relative w-full max-w-md bg-gradient-to-b from-[#1C1C32] to-[#12121f] border border-[#2A2A45] rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-[#FFD700]/10 blur-3xl pointer-events-none" />
            <button type="button" onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/[0.05] border border-white/[0.1] flex items-center justify-center text-white/40 hover:text-white/70 hover:bg-white/[0.1] transition-colors z-10"><X className="w-4 h-4" /></button>

            <div className="relative p-6 pt-8">
              {/* Header */}
              <div className="flex flex-col items-center mb-6">
                <motion.div animate={{ rotate: [0, -5, 5, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FFD700]/20 to-[#F37020]/20 border border-[#FFD700]/30 flex items-center justify-center mb-3"><Flame className="w-8 h-8 text-[#FFD700]" /></motion.div>
                <h2 className="text-xl font-bold text-white">{streakData.streakName}</h2>
                <p className="text-sm text-white/40 mt-1">{streakData.currentStreak > 0 ? `${streakData.currentStreak} day streak!` : "Start your streak today!"}</p>
              </div>

              {/* Today's Reward */}
              {!claimed && streakData.canClaimToday && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-5 p-4 rounded-xl bg-gradient-to-r from-[#FFD700]/10 to-[#F37020]/10 border border-[#FFD700]/20">
                  <div className="flex items-center justify-between">
                    <div><p className="text-xs text-[#FFD700]/70 uppercase tracking-wider font-medium">Today's Reward</p><p className="text-lg font-bold text-[#FFD700] mt-0.5">{getRewardLabel(streakData.todayReward.type, streakData.todayReward.amount)}</p></div>
                    <div className="w-12 h-12 rounded-xl bg-[#FFD700]/20 flex items-center justify-center"><Flame className="w-6 h-6 text-[#FFD700]" /></div>
                  </div>
                </motion.div>
              )}

              {/* Claim Success */}
              {claimed && claimResult && (
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="mb-5 p-4 rounded-xl bg-gradient-to-r from-emerald-500/10 to-emerald-600/10 border border-emerald-500/20">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center"><Sparkles className="w-5 h-5 text-emerald-400" /></div>
                    <div><p className="text-sm font-medium text-emerald-400">Claimed!</p><p className="text-xs text-emerald-400/70">+{getRewardLabel(claimResult.type, claimResult.amount)}</p></div>
                  </div>
                </motion.div>
              )}

              {/* Calendar Grid */}
              <div className="mb-5">
                <p className="text-xs text-white/30 uppercase tracking-wider font-medium mb-3">Past 30 Days</p>
                <div className="grid grid-cols-7 gap-1 mb-1">{WEEKDAYS.map((wd) => <div key={wd} className="text-center text-[9px] text-white/20 font-medium">{wd}</div>)}</div>
                <div className="grid grid-cols-7 gap-1">
                  {(() => { if (!streakData.history.length) return null; const startDay = new Date(streakData.history[0].date + "T12:00:00").getDay(); return Array.from({ length: startDay }).map((_, i) => <div key={`pad-${i}`} className="min-h-[52px]" />) })()}
                  {streakData.history.map((day, i) => <CalendarDay key={day.date} day={day} index={i} isCurrentDay={isToday(day.date)} />)}
                </div>
              </div>

              {/* Next Milestone */}
              {streakData.nextMilestone && !claimed && (
                <div className="mb-5 p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                  <div className="flex items-center gap-2"><Trophy className="w-4 h-4 text-[#FFD700]/60" /><p className="text-xs text-white/40"><span className="text-white/60">Day {streakData.nextMilestone.day}:</span> {getRewardLabel(streakData.nextMilestone.reward.type, streakData.nextMilestone.reward.amount)}</p></div>
                </div>
              )}

              {/* Buttons */}
              {!claimed && streakData.canClaimToday ? (
                <motion.button type="button" onClick={handleClaim} disabled={claiming} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[#FFD700] to-[#F37020] text-black font-bold text-sm shadow-lg shadow-[#FFD700]/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  {claiming ? <><Loader2 className="w-4 h-4 animate-spin" />Claiming...</> : <><Gift className="w-4 h-4" />Claim Reward</>}
                </motion.button>
              ) : (
                <motion.button type="button" onClick={onClose} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full py-3.5 rounded-xl bg-white/[0.08] border border-white/[0.1] text-white/70 font-medium text-sm hover:bg-white/[0.12] transition-colors">
                  {claimed ? "Continue" : "Come back tomorrow!"}
                </motion.button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
